import { Navigate, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { loginWithSiteAppId, showError } from "../../redux/common/actions";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import {
	isAuthenticated,
	hasSiteAppSession,
	resolveAndApplyUserLocale,
} from "i18n";

const ProtectedRoute = ({ component: Component, children, ...rest }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [shouldNavigate, setShouldNavigate] = useState(false);
	const me =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));
	const { pathname, search } = useLocation();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	//case for redirectedUrl with siteAppId
	const isRedirectUrl = search?.includes("siteAppId");
	const isFeedbackUrl = pathname?.includes("feedback");
	const params = new URLSearchParams(search);
	const siteAppId = params?.get("siteAppId");

	// Apply the signed-in user's effective locale whenever the active site-app
	// session changes. Language is session-scoped, not route-scoped: every
	// screen of a site-app session (operational and management alike) renders
	// in the same resolved locale, so the app never mixes languages between
	// e.g. the models page and the settings/profile pages. The marker
	// distinguishes each site app AND the no-session (fresh sign-in) state via
	// a sentinel, so the locale re-resolves right after login and when a
	// site-app session begins (SPA transitions, no reload). Site-app *switches*
	// full-reload elsewhere so the startup path covers those. Guarded per
	// context so it resolves once; resolveAndApplyUserLocale reloads only if
	// the language actually changes.
	useEffect(() => {
		if (!isAuthenticated()) return;
		const activeContext = hasSiteAppSession()
			? me?.siteAppID?.toString() || siteAppId
			: "__management__";
		try {
			if (sessionStorage.getItem("localeResolvedSiteApp") === activeContext) {
				return;
			}
			sessionStorage.setItem("localeResolvedSiteApp", activeContext);
		} catch {
			// storage unavailable — resolve anyway
		}
		resolveAndApplyUserLocale({ reload: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname, search]);

	useEffect(() => {
		// conditions to check if login is required
		const shouldLogin =
			!me?.position &&
			siteAppId &&
			isFeedbackUrl &&
			!isLoading &&
			!shouldNavigate;
		if (shouldLogin) {
			const handleLogin = async () => {
				setIsLoading(true);
				const response = await dispatch(loginWithSiteAppId(siteAppId, false));
				if (!response?.status) {
					setIsLoading(false);
					setShouldNavigate(false);
					dispatch(
						showError(
							response?.data ??
								"Cannot login to the site app. Please try again!"
						)
					);
					navigate("/app/portal");
					return;
				}
				setIsLoading(false);
				setShouldNavigate(true);
			};

			handleLogin();
		}
	}, [me, siteAppId, isFeedbackUrl, isLoading, shouldNavigate, dispatch]);

	useEffect(() => {
		if (shouldNavigate) {
			navigate(`${pathname}${search}`);

			setShouldNavigate(false);
		}
	}, [shouldNavigate, navigate, pathname, search]);

	if (isLoading) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
					flex: 1,
				}}
			>
				<CircularProgress size="50px" />
			</div>
		);
	}
	//me?.position to check siteApp authentication
	if ((me && !isRedirectUrl) || me?.position) return children;

	//redirect if not logged into a site
	if (!me) {
		return <Navigate to={`/login?redirectUrl=${encodeURIComponent(pathname + search)}`} />;
	}
};

export default ProtectedRoute;
